/**
 * Match Schedule Form Component
 * Reusable form for scheduling matches with date and time selection
 */

"use client";

import { DatePicker } from "@/components/ui/DatePicker";
import { useFeedbackPrompt } from "@/contexts/FeedbackPromptContext";
import { Location } from "@/types";
import {
  Button,
  Checkbox,
  Select,
  SelectItem,
  TimeInput,
} from "@heroui/react";
import { addToast } from "@heroui/toast";
import { Time } from "@internationalized/date";
import { startOfDay } from "date-fns";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

export type MatchScheduleStreamingValue = { courtNumber: number } | null;

interface MatchScheduleFormProps {
  onSubmit: (
    date: Date,
    locationId?: string,
    streaming?: MatchScheduleStreamingValue,
  ) => Promise<void>;
  onCancel?: () => void;
  onSuccess?: () => void;
  showDescription?: boolean;
  isLoading?: boolean;
  locations?: Location[];
  requireLocation?: boolean;
  /** Override label for the submit button */
  submitLabel?: string;
  /** Maximum date allowed for scheduling */
  maxDate?: Date;
  /** Initial streaming selection (used when rescheduling a match that has a stream). */
  initialStreaming?: { courtNumber: number } | null;
  /** Initial match date (used when rescheduling). */
  initialDate?: Date | null;
  /** Initial location id (used when rescheduling). */
  initialLocationId?: string | null;
}

export function MatchScheduleForm({
  onSubmit,
  onCancel,
  onSuccess,
  isLoading: externalIsLoading = false,
  locations = [],
  requireLocation = false,
  submitLabel,
  maxDate,
  initialStreaming,
  initialDate,
  initialLocationId,
}: MatchScheduleFormProps) {
  const currentHour = new Date().getHours();
  const t = useTranslations();
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initialDate ? startOfDay(initialDate) : startOfDay(new Date()),
  );
  const [selectedTime, setSelectedTime] = useState<Time>(
    initialDate
      ? new Time(initialDate.getHours(), initialDate.getMinutes())
      : new Time(currentHour + 1, 0),
  );
  const [selectedLocation, setSelectedLocation] = useState<string>(
    initialLocationId || locations[0]?.id || "",
  );
  const [streamingEnabled, setStreamingEnabled] = useState<boolean>(
    !!initialStreaming,
  );
  const [selectedCourt, setSelectedCourt] = useState<number | null>(
    initialStreaming?.courtNumber ?? null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { trackAction } = useFeedbackPrompt();

  const selectedLocationObj = useMemo(
    () => locations.find((l) => l.id === selectedLocation),
    [locations, selectedLocation],
  );

  const streamingAvailable = Boolean(
    selectedLocationObj?.striimy?.enabled &&
      selectedLocationObj.striimy.courts.length > 0,
  );

  // When the location changes, reset streaming UI unless the new location
  // supports streaming AND we have a compatible initial value.
  useEffect(() => {
    if (!streamingAvailable) {
      setStreamingEnabled(false);
      setSelectedCourt(null);
      return;
    }
    const courts = selectedLocationObj?.striimy?.courts ?? [];
    setSelectedCourt((current) => {
      if (current !== null && courts.includes(current)) return current;
      return null;
    });
  }, [selectedLocation, streamingAvailable, selectedLocationObj]);

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) return;
    if (requireLocation && !selectedLocation) return;
    if (streamingEnabled && selectedCourt === null) return;

    setIsSubmitting(true);
    try {
      const jsDate = new Date(selectedDate);
      jsDate.setHours(selectedTime.hour, selectedTime.minute, 0, 0);

      let streamingValue: MatchScheduleStreamingValue | undefined;
      if (streamingAvailable) {
        streamingValue =
          streamingEnabled && selectedCourt !== null
            ? { courtNumber: selectedCourt }
            : null;
      } else if (initialStreaming) {
        // Location switched to a non-streaming one — explicitly cancel.
        streamingValue = null;
      }

      await onSubmit(jsDate, selectedLocation || undefined, streamingValue);

      setSelectedDate(null);
      setSelectedTime(new Time(currentHour + 1, 0));
      setSelectedLocation("");
      setStreamingEnabled(false);
      setSelectedCourt(null);

      trackAction();
      onSuccess?.();
    } catch (error) {
      console.error("Failed to submit match schedule:", error);
      addToast({
        title: t("common.updateError", { subject: t("matches.title") }),
        color: "danger",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedDate(null);
    setSelectedTime(new Time(currentHour + 1, 0));
    setSelectedLocation("");
    setStreamingEnabled(false);
    setSelectedCourt(null);
    onCancel?.();
  };

  const isLoading = isSubmitting || externalIsLoading;

  const now = new Date();
  const today = startOfDay(now);
  const isToday =
    selectedDate && startOfDay(selectedDate).getTime() === today.getTime();
  const currentTime = new Time(now.getHours(), now.getMinutes());
  const isTimeInPast =
    isToday === true && selectedTime.compare(currentTime) <= 0;

  const minTime = isToday ? currentTime : undefined;

  const isValid =
    selectedDate &&
    selectedTime &&
    !isTimeInPast &&
    (!requireLocation || selectedLocation) &&
    (!streamingEnabled || selectedCourt !== null);

  return (
    <div className="space-y-3">
      <DatePicker
        label={t("matches.setMatchDate")}
        value={selectedDate}
        onChange={setSelectedDate}
        minDate={today}
        maxDate={maxDate}
      />

      <div className="flex gap-2 flex-col">
        <p className="text-secondary text-sm">
          {t("common.time")} <span className="text-red-500">*</span>
        </p>

        <TimeInput
          label={t("common.time")}
          value={selectedTime}
          onChange={(value) => value && setSelectedTime(value)}
          hourCycle={24}
          granularity="minute"
          isRequired
          hideTimeZone
          minValue={minTime}
          isInvalid={isTimeInPast}
          errorMessage={isTimeInPast ? t("matches.timeInPast") : undefined}
          classNames={{
            base: "medieval-input",
            inputWrapper:
              "bg-[var(--bg-deep)] border border-[var(--border-subtle)] hover:border-[var(--border-gold)]",
            input: "text-primary",
            label: "sr-only",
          }}
        />

        {locations.length > 0 && (
          <>
            <p className="text-secondary text-sm">
              {t("matches.location")} <span className="text-red-500">*</span>
            </p>
            <Select
              aria-label={t("matches.location")}
              placeholder={t("matches.selectLocation")}
              labelPlacement="outside"
              selectedKeys={selectedLocation ? [selectedLocation] : []}
              onSelectionChange={(keys) => {
                const selected = Array.from(keys)[0] as string;
                setSelectedLocation(selected || "");
              }}
              isRequired={requireLocation}
              disallowEmptySelection={false}
              className="w-full"
              classNames={{
                trigger:
                  "bg-[var(--bg-deep)] border border-[var(--border-subtle)] hover:border-[var(--border-gold)] cursor-pointer",
                value: "text-primary",
                label: "text-secondary text-sm",
                listbox: "bg-[var(--bg-deep)]",
                popoverContent:
                  "bg-[var(--bg-deep)] border border-[var(--border-subtle)]",
              }}
              popoverProps={{
                portalContainer:
                  typeof window !== "undefined" ? document.body : undefined,
              }}
            >
              {locations.map((location) => (
                <SelectItem
                  key={location.id}
                  className="text-primary hover:bg-[var(--bg-card)]"
                >
                  {location.name}
                </SelectItem>
              ))}
            </Select>
          </>
        )}

        {streamingAvailable && (
          <div className="flex flex-col gap-2 pt-1">
            <Checkbox
              isSelected={streamingEnabled}
              onValueChange={setStreamingEnabled}
              classNames={{
                label: "text-primary text-sm",
              }}
            >
              {t("matchSchedule.streamMatch")}
            </Checkbox>
            {streamingEnabled && (
              <Select
                aria-label={t("matchSchedule.court")}
                placeholder={t("matchSchedule.selectCourt")}
                labelPlacement="outside"
                selectedKeys={
                  selectedCourt !== null ? [String(selectedCourt)] : []
                }
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string | undefined;
                  setSelectedCourt(selected ? Number(selected) : null);
                }}
                isRequired
                disallowEmptySelection={false}
                className="w-full"
                classNames={{
                  trigger:
                    "bg-[var(--bg-deep)] border border-[var(--border-subtle)] hover:border-[var(--border-gold)] cursor-pointer",
                  value: "text-primary",
                  label: "text-secondary text-sm",
                  listbox: "bg-[var(--bg-deep)]",
                  popoverContent:
                    "bg-[var(--bg-deep)] border border-[var(--border-subtle)]",
                }}
                popoverProps={{
                  portalContainer:
                    typeof window !== "undefined" ? document.body : undefined,
                }}
              >
                {(selectedLocationObj?.striimy?.courts ?? []).map((n) => (
                  <SelectItem
                    key={String(n)}
                    className="text-primary hover:bg-[var(--bg-card)]"
                  >
                    {String(n)}
                  </SelectItem>
                ))}
              </Select>
            )}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button
            variant="flat"
            onPress={handleCancel}
            className="bg-transparent border border-[var(--border-subtle)] text-secondary hover:border-[var(--border-gold)] hover:text-[var(--gold)]"
          >
            {t("common.cancel")}
          </Button>
        )}
        <Button
          onPress={handleSubmit}
          isDisabled={!isValid || isLoading}
          isLoading={isLoading}
          className="bg-gradient-to-r from-[var(--gold-dark)] to-[var(--gold)] text-black font-semibold hover:shadow-lg hover:shadow-[var(--gold)]/50"
        >
          {submitLabel || t("matches.scheduleMatch")}
        </Button>
      </div>
    </div>
  );
}
